import { useContext, useState } from "react";
import { Star } from "lucide-react";
import { isEmpty } from 'lodash-es';

import { AppStateContext } from '@/state/AppStateContext';
import { saveRating } from "@/api/api";
import { CustomGPT } from '@/api/models';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface RateDialogProps {
  isRateDialogOpen: boolean;
  setIsRateDialogOpen: (isOpen: boolean) => void;
  selectedCustomGPT: CustomGPT | null;
}

const RateDialog = ({ isRateDialogOpen, setIsRateDialogOpen, selectedCustomGPT }: RateDialogProps) => {
  const appStateContext = useContext(AppStateContext);
  const [rating, setRating] = useState<number>(0);
  const [savingRate, setSavingRate] = useState<boolean>(false);

  /** 
   * Handle starting a conversation with a Custom GPT
   * Adding the Custom GPT to the user's list and navigating to chat
  **/
  const handleSaveRate = async () => {
    if (selectedCustomGPT) {
      setSavingRate(true);
      
      // Save rating
      await saveRating(selectedCustomGPT, rating);

      // Close dialog
      setRating(0);
      setSavingRate(false);
      setIsRateDialogOpen(false);
    }
  }

  /** 
   * Helper function to capitalize the first letter of first two words of Custom GPT name, if it does not have an icon image
   * 
   * @param {string} inputString - The Custom GPT's name
  **/
  const lettersAsIcon = (inputString: string) => {
    const words = inputString.split(' ');
    const initials = [];

    if (words.length >= 1) initials.push(words[0].charAt(0).toUpperCase());
    if (words.length >= 2) initials.push(words[1].charAt(0).toUpperCase());

    return initials.join('');
  }

  return (
    <Dialog open={isRateDialogOpen} onOpenChange={setIsRateDialogOpen}>
      <DialogContent className="flex flex-col max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-1/2 bg-secondary border-transparent rounded-3xl shadow-short p-0" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>

        <div className="flex flex-col max-h-[90vh] px-4">
          <div className="flex-1 overflow-x-hidden scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8">
            <div className="flex flex-col items-center">
              {!isEmpty(selectedCustomGPT?.image_path) ?
                <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${selectedCustomGPT?.image_path}`} className="w-24 h-24 rounded-full object-cover" alt={selectedCustomGPT?.name} />
              :
                <div className="avatar static placeholder">
                  <div className="bg-secondary w-24 rounded-full">
                    <span className="text-3xl">{lettersAsIcon(selectedCustomGPT?.name || '')}</span>
                  </div>
                </div>
              }

              <h1 className="text-3xl dark:text-white font-bold mt-4">{selectedCustomGPT?.name || ''}</h1>
              {!selectedCustomGPT?.pinned && <h6 className="text-sm text-accent mt-2">By: {selectedCustomGPT?.created_by_name || ''}</h6>}
              <h6 className="text-center text-sm mt-3">{selectedCustomGPT?.description || ''}</h6>

              <div className="w-full mt-8">
                <div className="dark:text-white font-bold px-1">Submit your rating</div>
                <div className="flex justify-around items-center mt-2 mx-4">
                  <Star className={`cursor-pointer ${rating >= 1 ? 'text-warning' : ''}`} size={36} fill={rating >= 1 ? '#ffc107' : ''} onClick={() => setRating(1)} />
                  <Star className={`cursor-pointer ${rating >= 2 ? 'text-warning' : ''}`} size={36} fill={rating >= 2 ? '#ffc107' : ''} onClick={() => setRating(2)} />
                  <Star className={`cursor-pointer ${rating >= 3 ? 'text-warning' : ''}`} size={36} fill={rating >= 3 ? '#ffc107' : ''} onClick={() => setRating(3)} />
                  <Star className={`cursor-pointer ${rating >= 4 ? 'text-warning' : ''}`} size={36} fill={rating >= 4 ? '#ffc107' : ''} onClick={() => setRating(4)} />
                  <Star className={`cursor-pointer ${rating >= 5 ? 'text-warning' : ''}`} size={36} fill={rating >= 5 ? '#ffc107' : ''} onClick={() => setRating(5)} />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-grow items-center justify-center w-full gap-4 mt-6 mb-2">
            <button type="button" className="btn btn-primary flex-grow" onClick={handleSaveRate} disabled={savingRate}>{savingRate ? <span className="loading loading-dots loading-lg text-primary"></span> : <>Submit Rating</>}</button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

export default RateDialog;
